import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

import {
  DRAFT_STATUS_HINT,
  canRetryParsing,
  draftStatusLabel,
  fetchDrafts,
  isClosed,
  retryDraft,
  withdrawDraft,
  type DraftStatus,
  type DraftView,
} from "../lib/drafts";
import { formatDateTime } from "../lib/format";
import { DemoModeBanner } from "../components/DemoModeBanner";
import { InWorkStamp } from "../components/InWorkStamp";

/**
 * Экран «Верификация» — СПИСОК черновиков (D-24, пункт «а»).
 *
 * ЗАЧЕМ ОН ПОЯВИЛСЯ. До этой задачи «Верификация» показывала ОДИН черновик — первый готовый из
 * очереди — и ничего больше. Владелец загрузил на площадку три комплекта подряд и увидел один: к
 * остальным не было пути в принципе. Причина не в ошибке кода, а в постановке: черновик был
 * спроектирован как путь одного объекта одного пользователя (docs/25 §2.1).
 *
 * ТРИ ВЕЩИ, КОТОРЫЕ СПИСОК ОБЯЗАН ГОВОРИТЬ, И ВСЕ ТРИ — ПРО ЛЮДЕЙ, А НЕ ПРО ЗАПИСИ:
 *   • КТО отправил и КОГДА — с этим человеком разговаривать, если со сканом непорядок;
 *   • В КАКОМ состоянии разбор — и что с этим делать прямо отсюда, не открывая карточку;
 *   • У КОГО черновик в работе и с какого времени — чтобы двое не делали одну работу молча.
 *
 * ПОИСКА И СТРАНИЦ ЗДЕСЬ НЕТ, И ЭТО ОСОЗНАННО (план §5.1). Черновик живёт часы, в очереди
 * единицы строк; поиск по очереди из пяти записей — работа впустую. Появится сотня — заведём
 * отдельную задачу, а не будем угадывать сейчас.
 */

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly drafts: DraftView[]; readonly demoNotice: string }
  | { readonly kind: "error"; readonly message: string };

/** Как часто спрашиваем о ходе разбора, пока в очереди есть «в обработке». */
const POLL_MS = 3000;

const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";

/** Оформление штампа состояния — токенами docs/07 (правило 9), без «сырых» цветов. */
const STATUS_CHIP: Record<DraftStatus, string> = {
  processing: "border-border bg-bg text-muted",
  ready: "border-done-fg/40 bg-done-bg text-done-fg",
  failed: "border-overdue-fg/40 bg-overdue-bg text-overdue-fg",
  confirmed: "border-done-fg/40 bg-done-bg text-done-fg",
  rejected: "border-border bg-bg text-muted",
  withdrawn: "border-border bg-bg text-muted",
};

export function VerificationListPage() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [showClosed, setShowClosed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await fetchDrafts(showClosed);
    if (!result.ok) {
      setState({ kind: "error", message: result.message });
      return;
    }
    setState({
      kind: "ready",
      drafts: result.value.drafts,
      demoNotice: result.value.ai.demoNotice,
    });
  }, [showClosed]);

  useEffect(() => {
    void load();
  }, [load]);

  // Опрос — только пока есть чего ждать, и прекращается сам: список, который больше не меняется,
  // дёргать сервер каждые три секунды незачем.
  const waiting = state.kind === "ready" && state.drafts.some((d) => d.status === "processing");
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [waiting, load]);

  async function onRetry(draft: DraftView): Promise<void> {
    // Подтверждение не формальность: повторный запрос к модели стоит денег, а нажать кнопку
    // случайно на списке из десятка строк слишком легко.
    const agreed = window.confirm(
      "Повторить разбор? Это новый платный запрос к модели.\n\n" +
        "Повтор помогает, если отказ был временным («высокий спрос», обрыв связи). " +
        "Если причина в настройках или доступе, повтор её не исправит.",
    );
    if (!agreed) return;
    setBusy(true);
    setError(null);
    const result = await retryDraft(draft.id);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setNotice("Разбор запущен заново.");
    await load();
  }

  async function onWithdraw(draft: DraftView, reason: string): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await withdrawDraft(draft.id, reason);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setNotice("Комплект отозван. Файлы сохранены, запись видна в закрытых.");
    await load();
  }

  if (state.kind === "loading") {
    return (
      <Shell busy>
        <p className="mt-4 text-muted">Загрузка…</p>
      </Shell>
    );
  }
  if (state.kind === "error") {
    return (
      <Shell>
        <p role="alert" className="mt-4 text-overdue-fg">
          {state.message}
        </p>
      </Shell>
    );
  }

  const shown = showClosed ? state.drafts : state.drafts.filter((d) => !isClosed(d.status));

  return (
    <Shell>
      <DemoModeBanner notice={state.demoNotice} />

      <label className="mt-3 flex items-center gap-2 text-[13px] text-ink">
        <input
          type="checkbox"
          checked={showClosed}
          onChange={(e) => setShowClosed(e.target.checked)}
        />
        Показать закрытые (подтверждённые, отклонённые, отозванные)
      </label>

      {error !== null && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="mt-3 text-[13px] text-done-fg">
          {notice}
        </p>
      )}

      {shown.length === 0 ? (
        <p className="mt-4 text-[14px] text-muted">
          Черновиков на верификацию нет. Они появляются здесь, когда разбор отправленного комплекта
          готов.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {shown.map((draft) => (
            <DraftRow
              key={draft.id}
              draft={draft}
              busy={busy}
              onRetry={onRetry}
              onWithdraw={onWithdraw}
            />
          ))}
        </ul>
      )}
    </Shell>
  );
}

function Shell({ children, busy = false }: { children: React.ReactNode; busy?: boolean }) {
  return (
    <section aria-labelledby="verify-title" aria-busy={busy ? "true" : undefined}>
      <h1 id="verify-title" className="text-[20px] font-bold text-ink">
        Верификация
      </h1>
      {children}
    </section>
  );
}

interface DraftRowProps {
  readonly draft: DraftView;
  readonly busy: boolean;
  readonly onRetry: (draft: DraftView) => Promise<void>;
  readonly onWithdraw: (draft: DraftView, reason: string) => Promise<void>;
}

/**
 * Строка очереди. Вся строка — ССЫЛКА на карточку черновика: до D-24 «открыть нужный» было
 * невыполнимым действием, и это главное, что строка обязана уметь.
 *
 * Кнопки «Повторить» и «Отозвать» стоят ЗДЕСЬ, а не только в карточке (план, пункт «б»): у
 * упавшего разбора карточки по сути нет — верифицировать нечего, — и гонять юриста внутрь, чтобы
 * он нажал единственную доступную кнопку, значит превращать выход из тупика в квест.
 */
function DraftRow({ draft, busy, onRetry, onWithdraw }: DraftRowProps) {
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const names = draft.files.map((f) => f.originalName).join(", ");

  return (
    <li className="rounded-[var(--radius-l)] border border-border bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-[var(--radius-s)] border px-2 py-0.5 text-[12px] font-bold ${STATUS_CHIP[draft.status]}`}
        >
          {draft.status === "processing" && (
            <Loader2 size={12} aria-hidden="true" className="animate-spin" />
          )}
          {draftStatusLabel(draft)}
        </span>
        <span className="text-[13px] text-muted">
          {formatDateTime(draft.createdAt)}
          {draft.createdByName === null ? "" : `, отправил ${draft.createdByName}`}
        </span>
        {draft.attempts > 1 && (
          <span className="text-[12px] text-muted">попыток разбора: {draft.attempts}</span>
        )}
      </div>

      {/*
        «В РАБОТЕ У X С ЧЧ:ММ» — ради этой строки половина задачи и делалась. Двое юристов,
        молча правящих один разбор, узнавали друг о друге только по отказу на кнопке.

        Штампом состояния, а не ссылочным синим (замечание приёмки 18.08.2026): рядом этой же
        краской набрано имя файла, ведущее на карточку, и надпись читалась как ещё одна ссылка.
      */}
      {draft.inWorkByName !== null && (
        <p className="mt-2">
          <InWorkStamp name={draft.inWorkByName} since={draft.inWorkSince} />
        </p>
      )}

      <p className="mt-2 text-[14px]">
        <Link to={`/verification/${draft.id}`} className="font-bold text-accent hover:underline">
          {names === "" ? "комплект без файлов" : names}
        </Link>
      </p>
      <p className="mt-1 text-[13px] text-muted">{DRAFT_STATUS_HINT[draft.status]}</p>

      {draft.status === "failed" && draft.errorText !== null && (
        <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-overdue-fg">{draft.errorText}</p>
      )}
      {draft.status === "rejected" && draft.rejectReason !== null && (
        <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-muted">
          Причина отклонения: {draft.rejectReason}
        </p>
      )}
      {draft.status === "withdrawn" && draft.withdrawReason !== null && (
        <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-muted">
          Отозвал {draft.withdrawnByName ?? "—"}: {draft.withdrawReason}
        </p>
      )}
      {draft.status === "confirmed" && draft.contractId !== null && (
        <Link to={`/contracts/${draft.contractId}`} className={`${btnSecondary} mt-3 inline-block`}>
          Открыть договор
        </Link>
      )}

      {draft.status === "failed" && !withdrawing && (
        <div className="mt-3 flex flex-wrap gap-2">
          {canRetryParsing(draft) && (
            <button
              type="button"
              onClick={() => void onRetry(draft)}
              disabled={busy}
              className={btnSecondary}
            >
              Повторить разбор
            </button>
          )}
          <button
            type="button"
            onClick={() => setWithdrawing(true)}
            disabled={busy}
            className={btnSecondary}
          >
            Отозвать
          </button>
        </div>
      )}

      {withdrawing && (
        <div className="mt-3 rounded-[var(--radius-m)] border border-border p-3">
          <label className="block text-[13px] text-muted" htmlFor={`wd-${draft.id}`}>
            Причина отзыва — она останется в журнале вместо записи (обязательна)
          </label>
          <textarea
            id={`wd-${draft.id}`}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void onWithdraw(draft, reason)}
              disabled={busy || reason.trim() === ""}
              className={btnSecondary}
            >
              Отозвать комплект
            </button>
            <button
              type="button"
              onClick={() => {
                setWithdrawing(false);
                setReason("");
              }}
              disabled={busy}
              className={btnSecondary}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
